import { cn } from '@/lib/utils';

/**
 * The shared body layout of the Calendar's floating hover surfaces.
 *
 * The milestone-cluster popover established the pattern: a light `--panel` card, a
 * heading row separated by a `--line-soft` rule, then muted detail lines beneath.
 * Bar and single-milestone hovers reuse it here so all three floating surfaces of
 * the Calendar read as one component family rather than three ad-hoc layouts.
 *
 * Layout only — the card surface itself (border, radius, shadow) comes from the
 * `panel` variant of {@link import('@/components/ui/tooltip').TooltipContent} or
 * from the popover content, so this composes into either without duplicating the
 * styling.
 *
 * The heading keeps the §4.2/§4.4 identity of what is hovered (the Initiative
 * title, or the milestone type) and the optional `icon` slot carries the same
 * colour-independent glyph the hovered element draws, so the association survives
 * without relying on colour (R10.6, R18.8).
 */
export function CalendarHoverCardBody({
  title,
  icon,
  className,
  children,
}: {
  readonly title: string;
  /** A decorative glyph echoing the hovered element (diamond, Platform swatch). */
  readonly icon?: React.ReactNode;
  readonly className?: string | undefined;
  /** The detail lines: self-describing, muted, one per line. */
  readonly children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={cn('flex min-w-[180px] max-w-[300px] flex-col', className)}>
      {/* The 8px heading rhythm is the milestone-cluster popover's, so the two
          surfaces line up when both are open. */}
      <div className="border-line-soft flex items-start gap-2 border-b pb-2">
        {icon !== undefined ? <span className="mt-[3px] shrink-0">{icon}</span> : null}
        <span className="text-txt text-[11px] font-semibold leading-[1.4]">{title}</span>
      </div>
      <div className="text-muted-foreground flex flex-col gap-[2px] pt-2 text-[11px] leading-[1.5]">
        {children}
      </div>
    </div>
  );
}
